import { StressType, SyllableContext } from "./checkSyllableContext";

/**
 * if prev & next aren't set, returns something like 'S' or '1',
 * else surrounds current with _ & adds prev before & next after, e.g., 'S_U_' or 'SU_U_S'
 */
export function syllableContextToString(context: SyllableContext): string {
  const current = context.current === 1 ? '1'
    : context.current === -1 ? '-1'
      : stringFromStressType(context.current);
  if (!context.prev && !context.next) {
    return current;
  }

  const prev = context.prev === undefined ? ''
    : context.prev.reverse().map(type => stringFromStressType(type)).join('');
  const next = context.next === undefined ? ''
    : context.next.map(type => stringFromStressType(type)).join('');
  return `${prev}_${current}_${next}`;
}

function stringFromStressType(type: StressType): string {
  return type === 'stressed' ? 'S'
    : type === 'unstressed' ? 'U'
      : 'A';
}

/**
 * parse serialized SyllableContext
 */
export function stringToSyllableContext(s: string): SyllableContext | SyllableError {
  const first = s.indexOf('_');
  const last = s.lastIndexOf('_');
  if (first === -1 || last === -1) {
    return stringToCurrent(s);
  } else if (first === last) {
    return { error: 'unrecognized syllable context', unknown: s };
  }

  const sPrev = s.substring(0, first);
  const sCurrent = s.substring(first + 1, last);
  const sNext = s.substring(last + 1);

  const prev = stringToStressTypes(sPrev);
  if (prev !== undefined && 'error' in prev) {
    return prev;
  }
  prev?.reverse();
  const current = stringToCurrent(sCurrent);
  if ('error' in current) {
    return current;
  }
  const next = stringToStressTypes(sNext);
  if (next !== undefined && 'error' in next) {
    return next;
  }

  return next === undefined ? { ...current, prev }
    : prev === undefined ? { ...current, next }
      : { ...current, prev, next };
}

export interface SyllableError {
  readonly error: 'unrecognized syllable context';
  readonly unknown: string;
}

/** S | U | A | 1 | -1 | error */
function stringToCurrent(s: string): SyllableContext | SyllableError {
  if (s === '1') {
    return { current: 1 };
  } else if (s === '-1') {
    return { current: -1 };
  } else {
    const current = stringToStressType(s);
    if (current === false) {
      return { error: 'unrecognized syllable context', unknown: s };
    }
    return { current };
  }
}

/** [S|U|A] | undefined | error */
function stringToStressTypes(s: string): StressType[] | undefined | SyllableError {
  if (s === '') {
    return undefined;
  }
  const results: StressType[] = [];
  for (const c of s) {
    const type = stringToStressType(c);
    if (!type) {
      return { error: 'unrecognized syllable context', unknown: c };
    }
    results.push(type);
  }
  return results;
}

/** S | U | A | false */
function stringToStressType(s: string): StressType | false {
  switch (s) {
    case 'S': return 'stressed';
    case 'U': return 'unstressed';
    case 'A': return 'any';
    default: return false;
  }
}
